import { Component, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { AbstractGridComponent } from '@unity/abstract/abstract-grid.component';
import { ReportDetailedDto, ReportsClient } from '@unity/clients';
import { TypedDirective } from '@unity/directives/typed.directive';
import { DateTimezonePipe } from '@unity/pipes/date-timezone.pipe';
import { DownloadService } from '@unity/services/download.service';
import { GridSharedComponent } from '@unity/shared/composed/grid-shared/grid-shared.component';
import { ButtonDownloadSharedComponent } from '@unity/shared/presentation/button-download-shared/button-download-shared.component';
import { SortConfig } from '@unity/utils/types';
import { take, tap } from 'rxjs';

@Component({
  selector: 'unity-grid-report-shared',
  standalone: true,
  imports: [KENDO_BUTTON, KENDO_ICONS, KENDO_GRID, DateTimezonePipe, GridSharedComponent, ButtonDownloadSharedComponent, TypedDirective],
  templateUrl: './grid-report-shared.component.html',
  styleUrl: './grid-report-shared.component.scss',
})
export class GridReportSharedComponent extends AbstractGridComponent<ReportDetailedDto> {
  private readonly reportsClient = inject(ReportsClient);
  private readonly downloadService = inject(DownloadService);

  protected resource = rxResource({
    params: () => {
      const query = this.$gridifyQuery();
      return query ? { query } : undefined;
    },
    stream: ({ params: { query } }) => this.reportsClient.getAllAsDetailed(query.page, query.pageSize, query.orderBy, query.filter),
  });

  protected readonly $activeDownloadIdSet = signal<Set<number>>(new Set());

  protected downloadReport(report: ReportDetailedDto) {
    this.$activeDownloadIdSet.update(prev => prev.add(report.id));
    this.reportsClient
      .downloadOne(report.id)
      .pipe(
        take(1),
        tap(res => this.downloadService.saveFile(res))
      )
      .subscribe({
        complete: () =>
          this.$activeDownloadIdSet.update(prev => {
            prev.delete(report.id);
            return prev;
          }),
      });
  }

  protected defaultSort: SortConfig[] = [
    {
      field: 'created_dt',
      dir: 'desc',
    },
  ];
}
